import { useState, useEffect, useCallback } from 'react'
import {
  backendBuilderApi,
  type ApiUserBackend, type ApiDbTable, type ApiDbColumn, type ApiApiEndpoint,
} from '../utils/api'

// ── §40+§41+§44: Backend Builder ─────────────────────────────────────────────

const COL_TYPES = ['text', 'integer', 'bigint', 'boolean', 'float', 'decimal', 'json', 'jsonb', 'timestamp', 'date', 'uuid', 'bytea']
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const AUTH_MODES = ['none', 'jwt', 'apiKey']

const METHOD_COLOR: Record<string, string> = {
  GET: '#22c55e', POST: '#3b82f6', PUT: '#f59e0b', PATCH: '#8b5cf6', DELETE: '#ef4444',
}
const METHOD_BG: Record<string, string> = {
  GET: '#f0fdf4', POST: '#eff6ff', PUT: '#fffbeb', PATCH: '#f5f3ff', DELETE: '#fef2f2',
}

function MethodBadge({ method }: { method: string }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 5,
      background: METHOD_BG[method] ?? '#f3f4f6',
      color: METHOD_COLOR[method] ?? '#374151',
      border: `1px solid ${METHOD_COLOR[method] ?? '#d1d5db'}33`,
      letterSpacing: '0.05em',
    }}>
      {method}
    </span>
  )
}

// ── Column editor ─────────────────────────────────────────────────────────────

function ColumnRow({
  col, onUpdate, onDelete,
}: {
  col: ApiDbColumn
  onUpdate: (id: string, dto: Partial<ApiDbColumn>) => void
  onDelete: (id: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(col.name)
  const [type, setType] = useState(col.type)

  function save() {
    onUpdate(col.id, { name, type })
    setEditing(false)
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 10px', borderBottom: '1px solid var(--border)',
      fontSize: 12, color: 'var(--text-primary)',
    }}>
      {col.isPrimary && <span title="Primary key" style={{ color: '#f59e0b', fontSize: 11 }}>🔑</span>}
      {!col.isPrimary && col.isUnique && <span title="Unique" style={{ color: '#8b5cf6', fontSize: 11 }}>◆</span>}
      {!col.isPrimary && !col.isUnique && <span style={{ color: 'var(--text-muted)', fontSize: 11, width: 14 }}>·</span>}

      {editing ? (
        <>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            style={{ flex: 1, padding: '2px 6px', border: '1px solid var(--accent)', borderRadius: 4, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)' }}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
          />
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            style={{ padding: '2px 4px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, background: 'var(--bg-2)', color: 'var(--text-primary)' }}
          >
            {COL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={save} style={{ padding: '2px 8px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>✓</button>
          <button onClick={() => setEditing(false)} style={{ padding: '2px 6px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>✕</button>
        </>
      ) : (
        <>
          <span style={{ flex: 1 }}>{col.name}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 11, minWidth: 60 }}>{col.type}</span>
          {!col.nullable && <span title="Not null" style={{ fontSize: 10, color: '#ef4444' }}>NN</span>}
          <button onClick={() => setEditing(true)} style={{ padding: '1px 6px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', opacity: 0.6 }}>✎</button>
          <button
            onClick={() => { if (confirm(`Удалить колонку "${col.name}"?`)) onDelete(col.id) }}
            style={{ padding: '1px 6px', background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer', opacity: 0.6 }}
          >✕</button>
        </>
      )}
    </div>
  )
}

// ── Table card ────────────────────────────────────────────────────────────────

function TableCard({
  table, backendId, onTableUpdated, onTableDeleted,
}: {
  table: ApiDbTable
  backendId: string
  onTableUpdated: (t: ApiDbTable) => void
  onTableDeleted: (id: string) => void
}) {
  const [open, setOpen] = useState(true)
  const [addingCol, setAddingCol] = useState(false)
  const [newColName, setNewColName] = useState('')
  const [newColType, setNewColType] = useState('text')
  const [columns, setColumns] = useState(table.columns)

  async function handleAddCol() {
    if (!newColName.trim()) return
    try {
      const col = await backendBuilderApi.addColumn(backendId, table.id, { name: newColName.trim(), type: newColType })
      setColumns(prev => [...prev, col])
      onTableUpdated({ ...table, columns: [...columns, col] })
      setNewColName('')
      setAddingCol(false)
    } catch (e) { alert(String(e)) }
  }

  async function handleUpdateCol(colId: string, dto: Partial<ApiDbColumn>) {
    try {
      const updated = await backendBuilderApi.updateColumn(backendId, table.id, colId, dto)
      setColumns(prev => prev.map(c => c.id === colId ? updated : c))
    } catch (e) { alert(String(e)) }
  }

  async function handleDeleteCol(colId: string) {
    try {
      await backendBuilderApi.removeColumn(backendId, table.id, colId)
      setColumns(prev => prev.filter(c => c.id !== colId))
    } catch (e) { alert(String(e)) }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
      {/* Header */}
      <div
        style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', background: 'var(--bg-2)', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ fontSize: 13, marginRight: 6, color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{table.name}</span>
        {table.displayName && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 8 }}>{table.displayName}</span>}
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 8 }}>{columns.length} колонок</span>
        <button
          onClick={e => { e.stopPropagation(); if (confirm(`Удалить таблицу "${table.name}"?`)) onTableDeleted(table.id) }}
          style={{ padding: '1px 6px', background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}
        >✕</button>
      </div>

      {/* Columns */}
      {open && (
        <div style={{ background: 'var(--bg-1)' }}>
          {columns.map(col => (
            <ColumnRow
              key={col.id}
              col={col}
              onUpdate={handleUpdateCol}
              onDelete={handleDeleteCol}
            />
          ))}

          {/* Add column */}
          {addingCol ? (
            <div style={{ display: 'flex', gap: 6, padding: '6px 10px', borderTop: '1px solid var(--border)', alignItems: 'center' }}>
              <input
                placeholder="column_name"
                value={newColName}
                onChange={e => setNewColName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void handleAddCol(); if (e.key === 'Escape') setAddingCol(false) }}
                style={{ flex: 1, padding: '4px 8px', border: '1px solid var(--accent)', borderRadius: 5, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)' }}
                autoFocus
              />
              <select
                value={newColType}
                onChange={e => setNewColType(e.target.value)}
                style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, background: 'var(--bg-2)', color: 'var(--text-primary)' }}
              >
                {COL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={() => void handleAddCol()} style={{ padding: '4px 10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>Добавить</button>
              <button onClick={() => setAddingCol(false)} style={{ padding: '4px 8px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>Отмена</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingCol(true)}
              style={{ width: '100%', padding: '6px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', textAlign: 'left', paddingLeft: 12, borderTop: '1px dashed var(--border)' }}
            >
              + Добавить колонку
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Endpoint row ──────────────────────────────────────────────────────────────

function EndpointRow({
  ep, backendId, onUpdated, onDeleted,
}: {
  ep: ApiApiEndpoint
  backendId: string
  onUpdated: (ep: ApiApiEndpoint) => void
  onDeleted: (id: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [method, setMethod] = useState(ep.method)
  const [path, setPath] = useState(ep.path)
  const [desc, setDesc] = useState(ep.description ?? '')
  const [auth, setAuth] = useState(ep.auth)

  async function save() {
    try {
      const updated = await backendBuilderApi.updateEndpoint(backendId, ep.id, { method, path, description: desc || undefined, auth })
      onUpdated(updated)
      setEditing(false)
    } catch (e) { alert(String(e)) }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
      {editing ? (
        <div style={{ padding: '10px 12px', background: 'var(--bg-2)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={method} onChange={e => setMethod(e.target.value)}
              style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 12, fontWeight: 700, background: 'var(--bg-1)', color: METHOD_COLOR[method] ?? 'var(--text-primary)' }}>
              {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input value={path} onChange={e => setPath(e.target.value)}
              placeholder="/api/path"
              style={{ flex: 1, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)' }}
            />
            <select value={auth} onChange={e => setAuth(e.target.value)}
              style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, background: 'var(--bg-1)', color: 'var(--text-primary)' }}>
              {AUTH_MODES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <input value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="Описание (необязательно)"
            style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, background: 'var(--bg-1)', color: 'var(--text-primary)' }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => void save()} style={{ padding: '4px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>Сохранить</button>
            <button onClick={() => setEditing(false)} style={{ padding: '4px 10px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Отмена</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-1)' }}>
          <MethodBadge method={ep.method} />
          <span style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{ep.path}</span>
          {ep.description && <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{ep.description}</span>}
          {ep.auth !== 'none' && (
            <span style={{ fontSize: 10, padding: '2px 6px', background: '#fef9c3', color: '#854d0e', border: '1px solid #fef08a', borderRadius: 4 }}>
              🔒 {ep.auth}
            </span>
          )}
          <button onClick={() => setEditing(true)} style={{ padding: '2px 6px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>✎</button>
          <button
            onClick={() => { if (confirm(`Удалить endpoint ${ep.method} ${ep.path}?`)) onDeleted(ep.id) }}
            style={{ padding: '2px 6px', background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}
          >✕</button>
        </div>
      )}
    </div>
  )
}

// ── §45 Realtime / WebSocket tab ─────────────────────────────────────────────

const WS_CHANNEL_TYPES = [
  { value: 'broadcast', label: 'Broadcast', desc: 'Публикация сообщений всем подключённым клиентам' },
  { value: 'presence', label: 'Presence', desc: 'Отслеживание подключённых пользователей (онлайн-статус)' },
  { value: 'private', label: 'Private', desc: 'Приватный канал с авторизацией' },
]

const MQTT_QOS_LEVELS = [
  { value: 0, label: 'QoS 0 — At most once' },
  { value: 1, label: 'QoS 1 — At least once' },
  { value: 2, label: 'QoS 2 — Exactly once' },
]

function RealtimeTab({ backendId }: { backendId: string }) {
  const [wsEnabled, setWsEnabled] = useState(false)
  const [mqttEnabled, setMqttEnabled] = useState(false)
  const [wsChannels, setWsChannels] = useState([
    { id: '1', name: 'device-telemetry', type: 'broadcast', authRequired: false },
    { id: '2', name: 'user-notifications', type: 'private', authRequired: true },
  ])
  const [mqttBrokerUrl, setMqttBrokerUrl] = useState('mqtt://localhost:1883')
  const [mqttTopics, setMqttTopics] = useState([
    { id: '1', topic: 'sensors/+/temperature', qos: 1, description: 'Temperature readings' },
    { id: '2', topic: 'devices/+/status', qos: 0, description: 'Device online/offline status' },
  ])
  const [addingChannel, setAddingChannel] = useState(false)
  const [newChannelName, setNewChannelName] = useState('')
  const [newChannelType, setNewChannelType] = useState('broadcast')
  const [addingTopic, setAddingTopic] = useState(false)
  const [newTopicStr, setNewTopicStr] = useState('')
  const [saved, setSaved] = useState(false)

  function handleSave() {
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Architecture note */}
      <div style={{ padding: '12px 14px', background: 'rgba(91,106,240,0.08)', borderRadius: 10, border: '1px solid rgba(91,106,240,0.2)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>⚡ Архитектура реального времени</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Browser → Your Backend → WebSocket Gateway → Industrial Controller / IoT Device.
          Прямое подключение browser → device без security layer не допускается.
        </div>
      </div>

      {/* WebSocket section */}
      <div style={{ background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: wsEnabled ? '1px solid var(--border)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14 }}>🔌</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>WebSocket Channels</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Realtime pub/sub каналы для вашего backend (ID: {backendId.slice(0, 8)})</div>
            </div>
          </div>
          <button
            onClick={() => setWsEnabled(v => !v)}
            style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', background: wsEnabled ? '#22c55e' : '#d1d5db', position: 'relative', flexShrink: 0 }}
          >
            <span style={{ position: 'absolute', top: 2, left: wsEnabled ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
          </button>
        </div>

        {wsEnabled && (
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {wsChannels.map(ch => (
              <div key={ch.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-1)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-primary)', flex: 1 }}>{ch.name}</span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: ch.type === 'broadcast' ? '#dbeafe' : ch.type === 'presence' ? '#dcfce7' : '#fef3c7', color: ch.type === 'broadcast' ? '#1d4ed8' : ch.type === 'presence' ? '#16a34a' : '#92400e', fontWeight: 700 }}>
                  {ch.type}
                </span>
                {ch.authRequired && <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 700 }}>🔒 AUTH</span>}
                <button onClick={() => setWsChannels(prev => prev.filter(c => c.id !== ch.id))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: '0 4px' }}>✕</button>
              </div>
            ))}
            {WS_CHANNEL_TYPES.map(ct => (
              <div key={ct.value} style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                <strong>{ct.label}:</strong> {ct.desc}
              </div>
            ))}
            {addingChannel ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <input
                  value={newChannelName}
                  onChange={e => setNewChannelName(e.target.value)}
                  placeholder="channel-name"
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--accent)', borderRadius: 7, fontSize: 12, fontFamily: 'monospace', background: 'var(--bg-1)', color: 'var(--text-primary)' }}
                  autoFocus
                />
                <select value={newChannelType} onChange={e => setNewChannelType(e.target.value)} style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12, background: 'var(--bg-2)', color: 'var(--text-primary)' }}>
                  {WS_CHANNEL_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
                <button
                  onClick={() => {
                    if (newChannelName.trim()) {
                      setWsChannels(prev => [...prev, { id: Date.now().toString(), name: newChannelName.trim(), type: newChannelType, authRequired: newChannelType === 'private' }])
                      setNewChannelName('')
                      setAddingChannel(false)
                    }
                  }}
                  style={{ padding: '6px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  Добавить
                </button>
                <button onClick={() => { setAddingChannel(false); setNewChannelName('') }} style={{ padding: '6px 10px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Отмена</button>
              </div>
            ) : (
              <button onClick={() => setAddingChannel(true)} style={{ padding: '7px 14px', background: 'var(--bg-2)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}>
                + Добавить канал
              </button>
            )}
          </div>
        )}
      </div>

      {/* MQTT section */}
      <div style={{ background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: mqttEnabled ? '1px solid var(--border)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14 }}>📡</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>MQTT Gateway</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Подключение к MQTT брокеру для IoT / промышленных устройств</div>
            </div>
          </div>
          <button
            onClick={() => setMqttEnabled(v => !v)}
            style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', background: mqttEnabled ? '#22c55e' : '#d1d5db', position: 'relative', flexShrink: 0 }}
          >
            <span style={{ position: 'absolute', top: 2, left: mqttEnabled ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', display: 'block' }} />
          </button>
        </div>

        {mqttEnabled && (
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 5 }}>Broker URL</label>
              <input
                value={mqttBrokerUrl}
                onChange={e => setMqttBrokerUrl(e.target.value)}
                placeholder="mqtt://broker:1883 или mqtts://..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12, fontFamily: 'monospace', background: 'var(--bg-1)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 5 }}>Подписки на топики</label>
              {mqttTopics.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '7px 10px', background: 'var(--bg-1)', borderRadius: 7, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-primary)', flex: 1 }}>{t.topic}</span>
                  <span style={{ fontSize: 10, padding: '2px 7px', background: '#f3f4f6', borderRadius: 5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>QoS {t.qos}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>{t.description}</span>
                  <button onClick={() => setMqttTopics(prev => prev.filter(x => x.id !== t.id))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: '0 4px' }}>✕</button>
                </div>
              ))}
              {addingTopic ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    value={newTopicStr}
                    onChange={e => setNewTopicStr(e.target.value)}
                    placeholder="sensors/+/data"
                    style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--accent)', borderRadius: 7, fontSize: 12, fontFamily: 'monospace', background: 'var(--bg-1)', color: 'var(--text-primary)' }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      if (newTopicStr.trim()) {
                        setMqttTopics(prev => [...prev, { id: Date.now().toString(), topic: newTopicStr.trim(), qos: 1, description: '' }])
                        setNewTopicStr('')
                        setAddingTopic(false)
                      }
                    }}
                    style={{ padding: '6px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >Добавить</button>
                  <button onClick={() => { setAddingTopic(false); setNewTopicStr('') }} style={{ padding: '6px 10px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Отмена</button>
                </div>
              ) : (
                <button onClick={() => setAddingTopic(true)} style={{ padding: '7px 14px', background: 'var(--bg-2)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}>
                  + Добавить топик
                </button>
              )}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
                Поддерживаются wildcards: <code>+</code> (один уровень), <code>#</code> (все уровни).
                QoS уровни: {MQTT_QOS_LEVELS.map(q => q.label).join(' | ')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Architecture diagram */}
      <div style={{ padding: '12px 14px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', lineHeight: 1.8 }}>
        <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, fontFamily: 'system-ui', fontSize: 12 }}>Архитектура подключения:</div>
        {'Browser'}<br />
        {'↓ HTTPS/WSS'}<br />
        {'Your BST Backend (this project)'}<br />
        {'↓ WebSocket / MQTT'}<br />
        {'Industrial Controller / IoT Device'}<br />
        <div style={{ marginTop: 8, fontFamily: 'system-ui', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1.5 }}>
          Security layer между browser и устройством — обязателен.
          Прямое подключение browser → device без авторизации не разрешено.
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        style={{ padding: '10px 20px', background: saved ? '#16a34a' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s' }}
      >
        {saved ? '✅ Сохранено' : '💾 Сохранить конфигурацию Realtime'}
      </button>
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function BackendBuilderPanel() {
  const [backends, setBackends] = useState<ApiUserBackend[]>([])
  const [selected, setSelected] = useState<ApiUserBackend | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'schema' | 'api' | 'realtime'>('schema')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [error, setError] = useState('')
  const [addingTable, setAddingTable] = useState(false)
  const [newTableName, setNewTableName] = useState('')
  const [addingEndpoint, setAddingEndpoint] = useState(false)
  const [newEpMethod, setNewEpMethod] = useState('GET')
  const [newEpPath, setNewEpPath] = useState('')
  const [schemaModal, setSchemaModal] = useState('')

  const loadBackends = useCallback(async () => {
    setLoading(true)
    try {
      const list = await backendBuilderApi.list()
      setBackends(list)
    } catch { setError('Не удалось загрузить backends') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { void loadBackends() }, [loadBackends])

  async function handleCreate() {
    if (!newName.trim()) return
    try {
      const b = await backendBuilderApi.create(newName.trim(), newDesc.trim() || undefined)
      const full = await backendBuilderApi.get(b.id)
      setBackends(prev => [b, ...prev])
      setSelected(full)
      setCreating(false)
      setNewName('')
      setNewDesc('')
    } catch (e) { setError(String(e)) }
  }

  async function handleSelectBackend(id: string) {
    try {
      const full = await backendBuilderApi.get(id)
      setSelected(full)
    } catch { setError('Не удалось загрузить backend') }
  }

  async function handleDeleteBackend(id: string) {
    if (!confirm('Удалить этот backend и все его данные?')) return
    try {
      await backendBuilderApi.remove(id)
      setBackends(prev => prev.filter(b => b.id !== id))
      if (selected?.id === id) setSelected(null)
    } catch (e) { setError(String(e)) }
  }

  async function handleAddTable() {
    if (!selected || !newTableName.trim()) return
    try {
      const t = await backendBuilderApi.addTable(selected.id, newTableName.trim())
      setSelected(prev => prev ? { ...prev, tables: [...prev.tables, t] } : prev)
      setNewTableName('')
      setAddingTable(false)
    } catch (e) { setError(String(e)) }
  }

  function handleTableUpdated(t: ApiDbTable) {
    setSelected(prev => prev ? { ...prev, tables: prev.tables.map(x => x.id === t.id ? t : x) } : prev)
  }

  async function handleTableDeleted(tableId: string) {
    if (!selected) return
    try {
      await backendBuilderApi.removeTable(selected.id, tableId)
      setSelected(prev => prev ? { ...prev, tables: prev.tables.filter(t => t.id !== tableId) } : prev)
    } catch (e) { setError(String(e)) }
  }

  async function handleAddEndpoint() {
    if (!selected || !newEpPath.trim()) return
    try {
      const ep = await backendBuilderApi.addEndpoint(selected.id, { method: newEpMethod, path: newEpPath.trim() })
      setSelected(prev => prev ? { ...prev, endpoints: [...prev.endpoints, ep] } : prev)
      setNewEpPath('')
      setAddingEndpoint(false)
    } catch (e) { setError(String(e)) }
  }

  function handleEndpointUpdated(ep: ApiApiEndpoint) {
    setSelected(prev => prev ? { ...prev, endpoints: prev.endpoints.map(x => x.id === ep.id ? ep : x) } : prev)
  }

  async function handleEndpointDeleted(epId: string) {
    if (!selected) return
    try {
      await backendBuilderApi.removeEndpoint(selected.id, epId)
      setSelected(prev => prev ? { ...prev, endpoints: prev.endpoints.filter(e => e.id !== epId) } : prev)
    } catch (e) { setError(String(e)) }
  }

  async function handleShowSql() {
    if (!selected) return
    try {
      const { sql } = await backendBuilderApi.schemaSql(selected.id)
      setSchemaModal(sql)
    } catch (e) { setError(String(e)) }
  }

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: 600 }}>
      {/* ── Left sidebar: backend list ── */}
      <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid var(--border)', paddingRight: 0 }}>
        <div style={{ padding: '0 0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Backends</h3>
            <button
              onClick={() => setCreating(v => !v)}
              style={{ padding: '4px 10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              + Создать
            </button>
          </div>

          {creating && (
            <div style={{ padding: '10px', background: 'var(--bg-2)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 10 }}>
              <input
                placeholder="Название backend"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void handleCreate() }}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--accent)', borderRadius: 5, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)', marginBottom: 6, boxSizing: 'border-box' }}
                autoFocus
              />
              <input
                placeholder="Описание (опционально)"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 5, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)', marginBottom: 6, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 5 }}>
                <button onClick={() => void handleCreate()} style={{ flex: 1, padding: '5px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>Создать</button>
                <button onClick={() => { setCreating(false); setNewName(''); setNewDesc('') }} style={{ padding: '5px 8px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>✕</button>
              </div>
            </div>
          )}

          {loading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Загрузка...</div>}

          {!loading && backends.length === 0 && !creating && (
            <div style={{ textAlign: 'center', padding: '40px 10px', color: 'var(--text-muted)', fontSize: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🗄️</div>
              Нет backends.<br />Создайте первый!
            </div>
          )}

          {backends.map(b => (
            <div
              key={b.id}
              onClick={() => void handleSelectBackend(b.id)}
              style={{
                padding: '9px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                background: selected?.id === b.id ? 'var(--accent)' : 'var(--bg-2)',
                border: `1px solid ${selected?.id === b.id ? 'var(--accent)' : 'var(--border)'}`,
                transition: 'all 0.12s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>🗄️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: selected?.id === b.id ? '#fff' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.name}
                  </div>
                  {b._count && (
                    <div style={{ fontSize: 10, color: selected?.id === b.id ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)', marginTop: 1 }}>
                      {b._count.tables} таблиц · {b._count.endpoints} endpoints
                    </div>
                  )}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); void handleDeleteBackend(b.id) }}
                  style={{ background: 'none', border: 'none', color: selected?.id === b.id ? 'rgba(255,255,255,0.7)' : '#ef4444', fontSize: 12, cursor: 'pointer', padding: '0 2px', opacity: 0.7 }}
                >✕</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, paddingLeft: 24, minWidth: 0 }}>
        {error && (
          <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 7, fontSize: 12, marginBottom: 12 }}>
            {error}
            <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b' }}>✕</button>
          </div>
        )}

        {!selected && (
          <div style={{ textAlign: 'center', paddingTop: 80, color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🗄️</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Backend Builder</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 360, margin: '0 auto' }}>
              Создайте backend для вашего веб-приложения.<br />
              Проектируйте схему базы данных и API endpoints визуально.
            </div>
          </div>
        )}

        {selected && (
          <>
            {/* Backend header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{selected.name}</h2>
                {selected.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{selected.description}</div>}
              </div>
              <div style={{ flex: 1 }} />
              {activeTab === 'schema' && (
                <button
                  onClick={() => void handleShowSql()}
                  style={{ padding: '6px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 600 }}
                >
                  📄 SQL
                </button>
              )}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: 16 }}>
              {(['schema', 'api', 'realtime'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{
                    padding: '8px 18px', background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === t ? 'var(--accent)' : 'transparent'}`,
                    marginBottom: -2, fontSize: 13, fontWeight: activeTab === t ? 700 : 500,
                    color: activeTab === t ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer',
                  }}
                >
                  {t === 'schema' ? '🗄️ Schema' : t === 'api' ? '🔌 API Endpoints' : '⚡ Realtime §45'}
                </button>
              ))}
            </div>

            {/* Schema tab */}
            {activeTab === 'schema' && (
              <div>
                {selected.tables.length === 0 && !addingTable && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
                    Нет таблиц. Добавьте первую!
                  </div>
                )}

                {selected.tables.map(t => (
                  <TableCard
                    key={t.id}
                    table={t}
                    backendId={selected.id}
                    onTableUpdated={handleTableUpdated}
                    onTableDeleted={handleTableDeleted}
                  />
                ))}

                {addingTable ? (
                  <div style={{ display: 'flex', gap: 8, padding: '10px 0', alignItems: 'center' }}>
                    <input
                      placeholder="table_name"
                      value={newTableName}
                      onChange={e => setNewTableName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void handleAddTable(); if (e.key === 'Escape') setAddingTable(false) }}
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--accent)', borderRadius: 7, fontSize: 13, background: 'var(--bg-1)', color: 'var(--text-primary)' }}
                      autoFocus
                    />
                    <button onClick={() => void handleAddTable()} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Добавить</button>
                    <button onClick={() => { setAddingTable(false); setNewTableName('') }} style={{ padding: '7px 10px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Отмена</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingTable(true)}
                    style={{ padding: '8px 14px', background: 'var(--bg-2)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}
                  >
                    + Добавить таблицу
                  </button>
                )}
              </div>
            )}

            {/* API Endpoints tab */}
            {activeTab === 'api' && (
              <div>
                {selected.endpoints.length === 0 && !addingEndpoint && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
                    Нет endpoints. Добавьте первый!
                  </div>
                )}

                {selected.endpoints.map(ep => (
                  <EndpointRow
                    key={ep.id}
                    ep={ep}
                    backendId={selected.id}
                    onUpdated={handleEndpointUpdated}
                    onDeleted={handleEndpointDeleted}
                  />
                ))}

                {addingEndpoint ? (
                  <div style={{ display: 'flex', gap: 8, padding: '10px 0', alignItems: 'center' }}>
                    <select
                      value={newEpMethod}
                      onChange={e => setNewEpMethod(e.target.value)}
                      style={{ padding: '7px 8px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, fontWeight: 700, background: 'var(--bg-2)', color: METHOD_COLOR[newEpMethod] ?? 'var(--text-primary)' }}
                    >
                      {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <input
                      placeholder="/api/resource"
                      value={newEpPath}
                      onChange={e => setNewEpPath(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void handleAddEndpoint(); if (e.key === 'Escape') setAddingEndpoint(false) }}
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--accent)', borderRadius: 7, fontSize: 13, fontFamily: 'monospace', background: 'var(--bg-1)', color: 'var(--text-primary)' }}
                      autoFocus
                    />
                    <button onClick={() => void handleAddEndpoint()} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Добавить</button>
                    <button onClick={() => { setAddingEndpoint(false); setNewEpPath('') }} style={{ padding: '7px 10px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Отмена</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingEndpoint(true)}
                    style={{ padding: '8px 14px', background: 'var(--bg-2)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontWeight: 600 }}
                  >
                    + Добавить endpoint
                  </button>
                )}
              </div>
            )}
            {/* §45 Realtime / WebSocket tab */}
            {activeTab === 'realtime' && (
              <RealtimeTab backendId={selected.id} />
            )}
          </>
        )}
      </div>

      {/* ── SQL modal ── */}
      {schemaModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setSchemaModal('')}
        >
          <div
            style={{ background: '#1a1a2e', borderRadius: 12, width: '100%', maxWidth: 700, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #333' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>📄 Schema SQL</span>
              <button onClick={() => setSchemaModal('')} style={{ background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <pre style={{ margin: 0, padding: '16px 18px', overflowY: 'auto', flex: 1, fontSize: 12, lineHeight: 1.7, color: '#a8ff78', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {schemaModal}
            </pre>
            <div style={{ padding: '10px 18px', borderTop: '1px solid #333', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { navigator.clipboard.writeText(schemaModal).catch(() => {}) }}
                style={{ padding: '6px 14px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
              >
                Скопировать SQL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
